import {
  ArrowRight,
  Award,
  Package,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { HeroLogoMorph } from "@/components/HeroLogoMorph";
import { cn } from "@/lib/utils";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { heroCopy } from "@/lib/siteCopy";

type HeroStat = {
  icon: LucideIcon;
  tone: string;
  iconClass: string;
};

const STAT_META: HeroStat[] = [
  { icon: Package, tone: "bg-[#FFF4DE]", iconClass: "text-amber-700" },
  { icon: Users, tone: "bg-[#F2E8F8]", iconClass: "text-violet-700" },
  { icon: ShieldCheck, tone: "bg-[#E7F0EC]", iconClass: "text-emerald-700" },
  { icon: Award, tone: "bg-[#E8EEF5]", iconClass: "text-sky-700" },
];

/** Add more logos here as you drop files into /public/marquee */
const TRUSTED_LOGOS = [
  { src: "/marquee/nic.webp", alt: "National Informatics Centre" },
  { src: "/marquee/tribal.jpg", alt: "Tribal Affairs" },
  { src: "/marquee/samku.png", alt: "Samku EV Services" },
  { src: "/marquee/sih.png", alt: "Smart India Hackathon" },
  { src: "/marquee/evc.png", alt: "Samku EVC" },
  { src: "/marquee/claude.jpg", alt: "Claude AI" },
  { src: "/marquee/hackwithindia.png", alt: "Hack With India" },
  { src: "/marquee/digitalindia.webp", alt: "Digital India" },
  { src: "/marquee/siemens.png", alt: "Siemens" },
];

export function Hero() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const lang = useSiteLanguage();
  const t = heroCopy[lang];
  const strip = [...TRUSTED_LOGOS, ...TRUSTED_LOGOS];

  return (
    <section
      id="hero"
      className="relative flex flex-col overflow-x-clip bg-background pt-16 lg:min-h-[100svh]"
    >
      <div className="mx-auto flex w-full max-w-[1400px] flex-1 flex-col gap-8 px-4 py-5 sm:gap-8 sm:px-6 sm:py-6 lg:justify-between lg:gap-8 lg:px-10 lg:py-7">
        <div className="grid items-center gap-6 sm:gap-7 lg:min-h-0 lg:flex-1 lg:grid-cols-[1.1fr_0.9fr] lg:items-stretch lg:gap-10 xl:gap-14">
          <div className="flex flex-col items-center justify-center text-center lg:items-start lg:text-left">
            {lang === "hi" ? (
              <h1 className="text-[2.65rem] font-bold leading-[1.05] tracking-[-0.035em] text-foreground sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] xl:leading-[1.02]">
                {t.line1}
                <br />
                <span className="text-muted-foreground">
                  {t.line3} {t.line4}
                </span>
              </h1>
            ) : (
              <h1 className="text-[2.65rem] font-bold leading-[1.05] tracking-[-0.035em] text-foreground sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem] xl:leading-[1.02]">
                {t.line1}{" "}
                <br className="hidden sm:block" />
                {t.line2}
                <br />
                <span className="text-muted-foreground">
                  {t.line3}{" "}
                  <br className="sm:hidden" />
                  {t.line4}
                </span>
              </h1>
            )}

            <p className="mt-3 max-w-[36rem] text-[14px] leading-relaxed text-muted-foreground sm:mt-4 sm:text-lg md:mt-5 md:text-xl">
              {t.sub}
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5 sm:mt-6 sm:gap-3 lg:justify-start">
              <Button
                size="lg"
                className="group h-11 rounded-lg px-6 text-sm sm:h-12 sm:px-7 sm:text-[15px]"
                onClick={() => navigate("/contact")}
              >
                {t.ctaPrimary}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-11 rounded-lg border-border bg-background px-6 text-sm sm:h-12 sm:px-7 sm:text-[15px]"
                onClick={() => navigate("/services")}
              >
                {t.ctaSecondary}
              </Button>
            </div>
          </div>

          <div className="flex w-full min-h-[28rem] items-center justify-center sm:min-h-[30rem] lg:h-full lg:min-h-[22rem] lg:justify-end xl:min-h-[26rem]">
            <HeroLogoMorph
              className="h-full w-full max-w-[min(100%,20rem)] sm:max-w-[22rem] lg:max-w-none"
              showControls={false}
            />
          </div>
        </div>

        <div className="shrink-0 space-y-8 sm:space-y-9 lg:space-y-10">
          <ul className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4 lg:gap-3.5">
            {STAT_META.map((stat, i) => {
              const Icon = stat.icon;
              const copy = t.stats[i]!;
              return (
                <li
                  key={copy.title}
                  className="flex items-center gap-2.5 rounded-xl bg-[#F4F4F5] px-3 py-2.5 dark:bg-muted/50 sm:gap-3 sm:px-3.5 sm:py-3"
                >
                  <span
                    className={cn(
                      "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9",
                      stat.tone,
                    )}
                  >
                    <Icon
                      className={cn("h-3.5 w-3.5 sm:h-4 sm:w-4", stat.iconClass)}
                      strokeWidth={1.75}
                    />
                  </span>
                  <div className="min-w-0 text-left">
                    <p className="truncate text-[12px] font-semibold leading-tight tracking-tight text-foreground sm:text-sm">
                      {copy.title}
                    </p>
                    <p className="mt-0.5 truncate text-[10px] leading-tight text-muted-foreground sm:text-xs">
                      {copy.description}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          <div>
            <p className="mx-auto max-w-3xl text-center text-[13px] leading-relaxed text-[#8A8A8A] sm:text-sm">
              {t.trusted}
            </p>

            <div className="work-marquee-track relative mt-6 overflow-hidden sm:mt-7">
              <div
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-background to-transparent sm:w-24"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-background to-transparent sm:w-24"
                aria-hidden
              />
              <div
                className={cn(
                  "flex w-max items-center gap-14 sm:gap-[4.5rem] lg:gap-20",
                  !reduce && "animate-work-marquee",
                )}
                style={{ animationDuration: "95s" }}
              >
                {[...strip, ...strip].map((logo, i) => (
                  <img
                    key={`${logo.src}-${i}`}
                    src={logo.src}
                    alt={i < strip.length ? logo.alt : ""}
                    className="h-9 w-auto shrink-0 object-contain opacity-60 mix-blend-multiply sm:h-10 dark:opacity-70 dark:invert dark:mix-blend-normal"
                    loading="lazy"
                    decoding="async"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
